import { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff } from 'lucide-react';
import LoginModal from '@/components/LoginModal';
import ForgotPasswordModal from "../components/ForgotPasswordModal";
import PausedAccountModal from '@/components/PausedAccountModal';
import { toast } from "sonner";
import { API_BASE_URL } from '@/lib/authFetch';

const Login = () => {
  const [showRegister, setShowRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPausedModal, setShowPausedModal] = useState(false);
  const [pendingLogin, setPendingLogin] = useState({ email: '', password: '' });
  const [reactivating, setReactivating] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Se veio da página Welcome com openRegister, já abre o modal de cadastro
  useEffect(() => {
    if (location.state?.openRegister) {
      setShowRegister(true);
    }
  }, [location.state]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (loading) return; // previne duplo submit
    setLoading(true);
    setEmailError('');
    setPasswordError('');
    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({
          username: email,
          password: password
        })
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("token", data.access_token); // Salva o token
        
        // Limpar valores antigos de acesso
        localStorage.removeItem("must_change_password");
        localStorage.removeItem("trial_expired");
        localStorage.removeItem("access_type");
        localStorage.removeItem("trial_end_date");
        
        // Salvar informações de acesso especial (cliente criado pelo admin)
        if (data.must_change_password) {
          localStorage.setItem("must_change_password", "true");
        }
        if (data.access_type) {
          localStorage.setItem("access_type", data.access_type);
        }
        // Só salvar trial_expired se realmente expirou
        if (data.trial_expired === true) {
          localStorage.setItem("trial_expired", "true");
        }
        if (data.trial_end_date) {
          localStorage.setItem("trial_end_date", data.trial_end_date);
        }
        
        // Inicializar timestamp de início da sessão para controle de expiração
        localStorage.setItem("session_start", Date.now().toString());
        
        // Buscar dados do usuário após login
        try {
          const userResponse = await fetch(`${API_BASE_URL}/users/me`, {
            headers: {
              "Authorization": `Bearer ${data.access_token}`
            }
          });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            localStorage.setItem("userName", userData.nome_completo);
            setUser(userData);
          }
        } catch {
          // Falha ao buscar /users/me não impede ir ao dashboard
        }
        navigate("/dashboard");
      } else {
        const data = await response.json();
        // Se for conta pausada, exibe modal
        if (response.status === 403 && data.detail && typeof data.detail === 'string' && data.detail.includes("pausada")) {
          setPendingLogin({ email, password });
          setShowPausedModal(true);
        } else if (data.detail && typeof data.detail === 'object') {
          // Erro específico por campo
          if (data.detail.field === 'email') {
            setEmailError(data.detail.message);
          } else if (data.detail.field === 'password') {
            setPasswordError(data.detail.message);
          }
        } else {
          // Erro genérico
          const errorMsg = typeof data.detail === 'string' ? data.detail : "E-mail ou senha incorretos";
          setEmailError(errorMsg);
        }
      }
    } catch {
      toast.error("Erro de conexão com o servidor. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // Função para tentar reativar e logar automaticamente
  const handleReactivateAndLogin = async () => {
    setReactivating(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/subscriptions/resume-with-credentials`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: pendingLogin.email,
          password: pendingLogin.password
        })
      });

      if (response.ok) {
        // Após reativar, faz login automaticamente
        const loginResponse = await fetch(`${API_BASE_URL}/login`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            username: pendingLogin.email,
            password: pendingLogin.password
          })
        });

        if (loginResponse.ok) {
          const data = await loginResponse.json();
          localStorage.setItem("token", data.access_token);
          // Inicializar timestamp de início da sessão para controle de expiração
          localStorage.setItem("session_start", Date.now().toString());
          
          // Buscar dados do usuário
          try {
            const userResponse = await fetch(`${API_BASE_URL}/users/me`, {           headers: {
                "Authorization": `Bearer ${data.access_token}`
              }
            });
            if (userResponse.ok) {
              const userData = await userResponse.json();
              localStorage.setItem("userName", userData.nome_completo);
            }
          } catch {
            // Falha ao buscar /users/me
          }
          setShowPausedModal(false);
          toast.success("Conta reativada com sucesso!");
          // Forçar redirecionamento imediato
          window.location.href = "/dashboard";
        } else {
          const data = await loginResponse.json();
          toast.error("Erro ao fazer login após reativação: " + (data.detail || "Erro desconhecido"));
        }
      } else {
        const data = await response.json();
        toast.error("Erro ao reativar assinatura: " + (data.detail || "Erro desconhecido"));
      }
    } catch {
      toast.error('Erro de conexão ao reativar assinatura. Verifique se o servidor está rodando.');
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-gradient-to-br from-protec-dark via-protec-dark-light to-protec-dark-lighter text-white relative overflow-hidden">
      {/* Glow dourado muito suave e discreto - deve ser o primeiro filho */}
       <div
        className="pointer-events-none z-0 absolute inset-0 w-full h-full"
        style={{
          background: 'radial-gradient(circle at 50% 50%, rgba(255, 230, 128, 0.08) 0%, rgba(255, 215, 64, 0.04) 40%, transparent 90%)',
          filter: 'blur(180px)',
          opacity: 0.7
        }}
      />
      {/* Topo esquerdo: Introdução */}
      <div className="absolute top-4 sm:top-6 left-4 sm:left-10 z-50">
        <Link
          to="/home"
          className="text-protec-gold text-xs sm:text-sm font-medium opacity-70 hover:opacity-100 transition-opacity underline cursor-pointer"
        >
          Introdução
        </Link>
      </div>
      {/* Lado esquerdo - Logo no mobile aparece no topo */}
      <div className="flex md:hidden flex-col justify-center items-center pt-16 pb-4 z-10">
        <img src="/logo.png" alt="Protec Bens Logo" className="h-20 w-20" />
      </div>
      <div className="hidden md:flex flex-1 flex-col justify-center items-center bg-transparent z-10">
        <div className="flex flex-row items-center gap-8">
          <img src="/logo.png" alt="Protec Bens Logo" className="h-32 lg:h-40 w-32 lg:w-40" />
          <span className="text-lg lg:text-xl font-normal text-gray-400 max-w-xs text-left">
            Líder em tecnologia analítica.
          </span>
        </div>
      </div>
      {/* Lado direito: Login */}
      <div className="flex flex-1 flex-col justify-center items-center w-full py-6 sm:py-12 px-4 z-10">
        <div className="w-full max-w-md">
          <h1 className="text-2xl sm:text-3xl font-bold gradient-text mb-2 text-center">Acesso ao Sistema</h1>
          <p className="text-gray-400 text-center mb-6 sm:mb-8 text-sm sm:text-base">Entre com seu e-mail e senha para acessar sua conta</p>
          <form onSubmit={handleLogin} className="space-y-4 sm:space-y-6">
            <div className="flex flex-col items-center">
              <div className="w-full max-w-xs">
                <Label htmlFor="email" className="text-gray-300 mb-1 block text-sm">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setEmailError(''); }}
                  className={`bg-protec-dark text-white w-full ${emailError ? 'border-red-500 border-2' : 'border-protec-gold/30'}`}
                  placeholder="seu@email.com"
                  required
                />
                {emailError && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">{emailError}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-full max-w-xs">
                <Label htmlFor="password" className="text-gray-300 mb-1 block text-sm">Senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={e => { setPassword(e.target.value); setPasswordError(''); }}
                    className={`bg-protec-dark text-white w-full pr-10 ${passwordError ? 'border-red-500 border-2' : 'border-protec-gold/30'}`}
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-protec-gold transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {passwordError && (
                  <p className="text-red-500 text-xs sm:text-sm mt-1">{passwordError}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col items-center">
              <Button type="submit" className="bg-gradient-gold text-black font-semibold hover:shadow-gold max-w-xs w-full py-2" disabled={loading}>
                {loading ? "Entrando..." : "Entrar"}
              </Button>
              {/* Botão esqueci minha senha */}
              <div className="flex justify-center mt-1 w-full">
                <button type="button" className="text-xs text-gray-400 hover:text-protec-gold transition-colors underline opacity-80 hover:opacity-100" onClick={() => setShowForgotPassword(true)}>
                  esqueci minha senha
                </button>
              </div>
            </div>
          </form>
          <div className="mt-4 sm:mt-6 text-center">
            <Button variant="ghost" className="text-protec-gold hover:underline text-sm sm:text-base" onClick={() => setShowRegister(true)}>
              Cadastre-se
            </Button>
          </div>
          {/* Modal de cadastro */}
          <LoginModal
            open={showRegister}
            onOpenChange={setShowRegister}
            onLoginSuccess={() => {}}
            defaultTab="register"
          />
          <ForgotPasswordModal open={showForgotPassword} onOpenChange={setShowForgotPassword} />
          <PausedAccountModal
            open={showPausedModal}
            onOpenChange={setShowPausedModal}
            onReactivate={handleReactivateAndLogin}
            reactivating={reactivating}
          />
        </div>
      </div>
    </div>
  );
};

export default Login; 